import { sendGAEvent } from "@next/third-parties/google";
import { type ClassValue, clsx } from "clsx";

import { withExtendedShadows } from "tailwind-extended-shadows-merge";
import { extendTailwindMerge } from "tailwind-merge";
import { ResponseError } from "./definitions";

const twMerge_ex = extendTailwindMerge(withExtendedShadows);

/** combines classes in a smart way */
export function cn(...inputs: ClassValue[]) {
  return twMerge_ex(clsx(inputs))
}

/** simple array randomize */
export function randomize(array: any[]) {
  return array.map((v: string) => ({ v, sort: Math.random() }))
    .sort((a: any, b: any) => a.sort - b.sort)
    .map(({ v }: { v: any }) => v);
}

/** Creates an error object - can be detect by using instanceOf ResponseError */
export function createErrorMsg(error: string) {
  return new ResponseError(error)
}

/** common REST readable errors */
export const restErrors = {
  /** no logged in user */
  "noLogin": createErrorMsg("פעולה נחסמה - את/ה לא מחובר"),
  /** user has no permission */
  "noPermission": createErrorMsg("פעולה חסומה - אין לך מספיק הרשאות"),

  /** could not found user ID */
  "noUserID": createErrorMsg("לא היה ניתן למצא מזהה משתמש"),
  /** could not found channel linked to user */
  "noLinkedYT": createErrorMsg("לא קיים ערוץ משוייך למשתמש"),
  /** could not found cron task */
  "noCronTask": createErrorMsg("לא היה ניתן למצא את התהליך"),

  /** generic server error */
  "serverError": createErrorMsg("שגיאת שרת בביצוע פעולה"),
}

/** basic card design for general components */
export const CardClass = cn(CardRawClass.replace("m-1 sm:m-4", ""), `p-2 sm:p-4 min-h-40 m-auto w-full min-w-[33%]`)
export const CardClass_BLUE_colors = "border-nfBlue-500/80 ring-nfBlue-500"


/** sleep promise for X seconds */
export function sleep(sec: number) { return new Promise(res => setTimeout(res, sec * 1000)) }

export const possibleGAEvents = ["subscribe", "login", "openStatistics", "search", "videoView", "shortView"] as const

export function GoogleAnalyticsEvent({ event_name, event_value = 1, event_label, search_term, non_interaction }:
  {
    /** The name of a custom event is whatever name you choose for the event. The name should describe what you intend to measure with the event. For example, if you're measuring donations, the name might be "donate." */
    event_name: typeof possibleGAEvents[number],
    /** the value to add for tracking - most likely 1*/
    event_value?: number,
    /** label shown for the event - used to identify action/relations/extra-info */
    event_label?: string,
    /** search term used only for search event */
    search_term?: string,
    /**avoids affecting bounce rate. */
    non_interaction?: boolean,
  }) {
  //https://developers.google.com/tag-platform/gtagjs/reference/events#search - prebuild options
  sendGAEvent("event", event_name, {
    value: event_value,
    event_label: event_label?.trim(),
    search_term: search_term?.trim(),
    non_interaction
  })
}

export type SetRequired<Type, Key extends keyof Type> = Type & Required<Pick<Type, Key>>;


/**
 * get a boolean state if a video is "new"
 * @param video the video object from prisma
 * @param days @optional customized days to check back if in "new range"
 * @returns 
 */
export function youtube_isVideoRecent(video: { createdAt: Date }, days = defaultRecentDays) {
  return new Date(video.createdAt) > new Date(Date.now() - days * 24 * 60 * 60 * 1000);
}
export const defaultRecentDays = 30;

/**
 * get youtube video link with tracking to nightfall site
 * @param videoId  the video id from youtube
 * @returns full url link
 */
export function youtube_getVideoLink(videoId: string) {
  return `https://www.youtube.com/watch?v=${videoId}&utm_source=https://www.nigthfall.co.il&utm_medium=referral&utm_campaign=nightfallSite`;
}

export function youtube_getVideoThumbnail(videoId: string) {
  return `https://img.youtube.com/vi_webp/${videoId}/hqdefault.webp`
}

export function api_getMinecraftRig(uuid: string, scale = 0.2, model: "mojavatar" | "sleeping" = "mojavatar") {
  return `https://starlightskins.lunareclipse.studio/render/${model}/${uuid}/full?renderScale=${scale || 0.2}&dropShadow=true&dirLightPos={"x":"-50","y":"10","z":"-200"}&dirLightIntensity=1&dirLightColor=fff8ef&globalLightColor=d8d1e0&globalLightIntensity=-0.1`
}


export function convertMsToISO8601(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / (24 * 3600));
  const hours = Math.floor((totalSeconds % (24 * 3600)) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  let isoString = 'P';
  if (days > 0) isoString += `${days}D`;

  if (hours > 0 || minutes > 0 || seconds > 0) {
    isoString += 'T';
    if (hours > 0) isoString += `${hours}H`;
    if (minutes > 0) isoString += `${minutes}M`;
    if (seconds > 0) isoString += `${seconds}S`;
  }

  return isoString;
}


import { CardRawClass } from "@/app/ui/imported/card";
import { UsefulLinkIcons } from "@prisma/client";
import { AiFillYoutube } from "react-icons/ai";
import { BiServer } from "react-icons/bi";
import { BsDiscord } from "react-icons/bs";
import { FaMicrophoneAlt, FaNewspaper, FaUserSecret } from "react-icons/fa";
import { MdLocalLibrary, MdPermMedia, MdSubtitles } from "react-icons/md";

export type UsefulLinksIcons = UsefulLinkIcons | undefined;
/** string names of icons => icon function parser */
export function UsefulLinksIcon(name: UsefulLinksIcons) {
  switch (name) {
    case "discord":
      return BsDiscord;
    case "youtube":
      return AiFillYoutube
    case "mic":
      return FaMicrophoneAlt;
    case "question":
      return MdLocalLibrary;
    case "media":
      return MdPermMedia;
    case "secret":
      return FaUserSecret;
    case "subtitle":
      return MdSubtitles;
    case "server":
      return BiServer;
    case "news":
      return FaNewspaper;
  }
}

/** self described lol */
export function secondsUntilMidnight() {
  const now = new Date();
  const options: Intl.DateTimeFormatOptions = { timeZone: "Asia/Jerusalem" };

  // Get the current time in Israel
  const israelNow = new Date(
    now.toLocaleString("en-US", options)
  );

  // Set midnight in Israel
  const israelMidnight = new Date(israelNow);
  israelMidnight.setHours(24, 0, 0, 0);

  return Math.floor((israelMidnight.getTime() - israelNow.getTime()) / 1000);
}

/** returns the time zone off set */
export function getTimezoneOffset() {
  return -new Date().getTimezoneOffset() / 60
}